<template>
  <div>
    <div :class="`desktop-popup position-absolute ${deskTopPopupClass}`">
      <component :is="transitionName" :duration="duration">
        <div v-if="show" :class="`profile__modal ${profileModalClass}`">
          <ImageBox
            :downloadCode="userData.downloadCode"
            :defaultImage="defaultImage"
            imageClass="profile__avatar"
            wrapperClass="flex-center"
            loadingClass="left-4 width-48 height-48 rounded-circle"
          />
          <div class="profile__title">
            <CopyTextWrapper
              :id="userData.fullName"
              :text="userData.fullName"
              textClass="cursor--pointer"
            />
          </div>
          <div class="profile__caption">
            <CopyTextWrapper
              :id="userData.mobile"
              :text="userData.mobile"
              textClass="cursor--pointer"
            />
          </div>
          <div class="profile__button" @click="goToProfile">
            {{ $lang.show_profile }}
          </div>
          <ul class="profile__links">
            <li
              v-if="showChangeInfo"
              class="profile__link"
              @click="goToChangeUserInformation"
            >
              <mdicon
                class="profile__icon"
                name="account-edit-outline"
                size="24"
              />
              <span class="profile__text">
                {{ $lang.change_user_information }}
              </span>
            </li>
            <li
              v-if="showLogout"
              class="profile__link"
              @click="handleOpenSignOutModal"
            >
              <mdicon class="profile__icon" name="logout" size="24" />
              <span class="profile__text">{{ $lang.logout }}</span>
            </li>
            <li
              v-if="hasVersion"
              class="color-complementary-300 font-size-12 flex-end"
            >
              {{ `${$lang.versionNumber}: ${currentVersion}` }}
            </li>
          </ul>
        </div>
      </component>
    </div>
    <MobilePopup :show="show" @setShow="handleShow">
      <template>
        <div class="text-center">
          <ImageBox
            :downloadCode="userData.downloadCode"
            :defaultImage="defaultImage"
            imageClass="profile__avatar"
            wrapperClass="flex-center"
            loadingClass="left-4 width-48 height-48 rounded-circle"
          />
        </div>
        <div class="profile__title">
          <CopyTextWrapper
            :id="userData.fullName"
            :text="userData.fullName"
            textClass="cursor--pointer"
          />
        </div>
        <div class="profile__caption">
          <CopyTextWrapper
            :id="userData.mobile"
            :text="userData.mobile"
            textClass="cursor--pointer"
          />
        </div>
        <div class="profile__button" @click="goToProfile">
          {{ $lang.show_profile }}
        </div>
        <ul class="profile__links">
          <li
            v-if="showChangeInfo"
            class="profile__link"
            @click="goToChangeUserInformation"
          >
            <mdicon
              class="profile__icon"
              name="account-edit-outline"
              size="24"
            />
            <span class="profile__text">
              {{ $lang.change_user_information }}
            </span>
          </li>
          <li
            v-if="showLogout"
            class="profile__link"
            @click="handleOpenSignOutModal"
          >
            <mdicon class="profile__icon" name="logout" size="24" />
            <span class="profile__text">{{ $lang.logout }}</span>
          </li>
          <li
            v-if="hasVersion"
            class="color-complementary-300 font-size-12 flex-end"
          >
            {{ `${$lang.versionNumber}: ${currentVersion}` }}
          </li>
        </ul>
      </template>
    </MobilePopup>
    <Teleport to="body">
      <Modal
        :show="isSignOutModal"
        :title="$lang.account_logout"
        :closeOnBackdrop="false"
        :hasClose="false"
        :hasFooter="true"
        :okButton="true"
        :okButtonText="$lang.logout"
        size="sm"
        okButtonVariant="filled-error"
        :ok="handleSignOut"
        @close="handleCloseSignOutModal"
      >
        <template #body>
          <div class="color-complementary-300 line-height-24">
            {{ $lang.account_logout_confirm_message }}
          </div>
        </template>
      </Modal>
    </Teleport>
  </div>
</template>

<script>
import Teleport from "vue2-teleport";
import ImageBox from "../image-box/ImageBox.vue";
import MobilePopup from "@/components/re-design/modal/MobilePopup.vue";
import Modal from "@/components/re-design/modal/Modal.vue";
import CopyTextWrapper from "@/components/re-design/copy-text-wrapper/CopyTextWrapper.vue";
import { pathOr } from "ramda";
import {
  checkScreenDevice,
  generateConfig,
  generateCurrentRoute,
  generateError,
  generateLang,
  generateStoreGetters,
  goToRoute,
  logout,
  storeDispatcher,
} from "@/shared/utils";
import { signOut } from "@/modules/auth/services";
import { computed, ref, toRefs } from "vue";
import { version } from "../../../../package";

export default {
  components: { Teleport, MobilePopup, Modal, CopyTextWrapper, ImageBox },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    showChangeInfo: {
      type: Boolean,
      default: true,
    },
    showLogout: {
      type: Boolean,
      default: true,
    },
    userData: {
      type: Object,
      default: null,
    },
    transitionName: {
      type: String,
      default: "slide-y-down-transition",
    },
    duration: {
      type: Number,
      default: 300,
    },
    deskTopPopupClass: {
      type: String,
      default: "",
    },
    profileCoverClass: {
      type: String,
      default: "",
    },
    profileModalClass: {
      type: String,
      default: "",
    },
    primaryKey: {
      type: String,
      default: "code",
    },
    hasVersion: {
      type: Boolean,
      default: true,
    },
    extraPersonParams: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    // bind props
    const { userData, primaryKey, extraPersonParams } = toRefs(props);

    const defaultImage = generateConfig([
      "images",
      "shared",
      "redesign",
      "empty",
      "noUser",
    ]);
    const currentVersion = ref(version);

    const currentUserInfo = computed(() => generateStoreGetters(["userInfo"]));

    const goToProfile = () => {
      const code = pathOr(null, [primaryKey.value], userData.value);
      const currentUserCode = pathOr(
        null,
        ["person", "code"],
        currentUserInfo.value
      );
      if (code === currentUserCode) {
        return goToRoute("profile", {}, {}, false);
      }

      const isOpenNewTab = checkScreenDevice(["desktop"]);
      goToRoute(
        "editPerson",
        { code, ...extraPersonParams.value },
        {},
        isOpenNewTab
      );
    };

    const handleShow = (val) => emit("setShow", val);

    const goToChangeUserInformation = async () => {
      const code = pathOr(null, ["code"], userData.value);
      await storeDispatcher(
        "mobileHeaderTitleText",
        generateLang(["change_user_information"])
      );
      await storeDispatcher("mobileHeaderBaseRoute", generateCurrentRoute());
      goToRoute("change-user-information", { code }, {}, false);
    };

    // handle sign out modal
    const isSignOutModal = ref(false);
    const handleOpenSignOutModal = () => (isSignOutModal.value = true);
    const handleCloseSignOutModal = () => (isSignOutModal.value = false);
    const handleSignOut = async () => {
      try {
        await signOut();
        handleCloseSignOutModal();
        setTimeout(() => logout(true), 700);
      } catch (error) {
        generateError(error);
      }
    };

    // return data to be used in template
    return {
      currentVersion,
      defaultImage,
      goToProfile,
      goToChangeUserInformation,
      isSignOutModal,
      handleOpenSignOutModal,
      handleCloseSignOutModal,
      handleSignOut,
      handleShow,
    };
  },
};
</script>
